import React, { useState } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Paper, Select } from '@mui/material';

import { isFeaturePatternTruss, ZoneSettings } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';

import { updateActiveZoneSettings } from '../../../../store/actions/editorActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { setTrussEditsPanel } from '../../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SingleZoneSettingsPanel } from '../../subpanels/properties/ZoneSettings';

export function RewebZonePanel() {
  const dispatch = useAppDispatch();
  const activeComponent = useAppSelector(getActiveComponentDesign)?.component;

  const [zoneIndex, setZoneIndex] = useState(() =>
    // if only one zone, select it by default
    isFeaturePatternTruss(activeComponent) && activeComponent.settings?.zoneSettings.length === 1 ? 0 : -1,
  );

  const [editedZoneSettings, setEditedZoneSettings] = useState<ZoneSettings | null>(null);

  if (!isFeaturePatternTruss(activeComponent) || !activeComponent.settings) return null;

  const allZoneSettings = activeComponent.settings.zoneSettings;
  const correspondingZoneSettings =
    activeComponent.zones.length === allZoneSettings.length ? allZoneSettings[zoneIndex] : allZoneSettings[0];

  const activeZoneSettings = editedZoneSettings ?? correspondingZoneSettings;
  const activeZoneGuid = activeComponent.zones[zoneIndex]?.guid;

  async function onApply() {
    if (!activeZoneSettings || !activeZoneGuid) return;

    dispatch(setTrussEditsPanel(null));
    await dispatch(updateActiveZoneSettings(activeZoneSettings, activeZoneGuid));
  }

  return (
    <PropertiesPanel title="Re-Web Zone">
      <FormControl size="small" component="fieldset" sx={{ marginX: 1 }}>
        <InputLabel htmlFor="zone-index">Zone</InputLabel>
        <Select
          value={zoneIndex}
          // MUI types are lying here, the value is a string
          onChange={(event) => setZoneIndex(parseInt(event.target.value as unknown as string, 10))}
          inputProps={{ id: 'zone-index' }}
          label="Zone"
        >
          {zoneIndex === -1 && (
            <MenuItem disabled value="-1">
              Choose a zone
            </MenuItem>
          )}

          {activeComponent.zones.map((zone, index) => (
            <MenuItem value={index.toString()} key={zone.guid}>{`Zone ${index + 1}`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {zoneIndex !== -1 && activeZoneSettings && (
        <SingleZoneSettingsPanel value={activeZoneSettings} onChange={setEditedZoneSettings} />
      )}

      <Paper elevation={1} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 1, borderRadius: 0 }}>
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(setTrussEditsPanel(null));
          }}
        >
          Cancel
        </Button>

        <Button variant="contained" disabled={zoneIndex === -1 || activeZoneSettings == null} onClick={onApply}>
          Generate new webs
        </Button>
      </Paper>
    </PropertiesPanel>
  );
}
